import { Injectable, Inject } from '@angular/core';
import { Http, Headers, ResponseContentType } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { CreateCaseData } from '../_models/SiteSelection';
import { PerformanceGapsData } from '../_models/PerformanceGaps';
import { SubUnitData } from '../_models/SubUnit';
import { BaseEndpoint } from '../../app.constants';
import { Equipment } from '../_models/Equipment';
import { CustomOpportunities } from '../_models/CustomOpportunities';
import {ExtendedHttpService} from '../../core/auth/extended-http.service';

@Injectable()
export class ManageCaseService {

  constructor(private http: Http, @Inject(BaseEndpoint) private baseApiEndpoint) {
  }

  public getCaseData(): Observable<any[]> {
    const customerSiteData = this.http.get(this.baseApiEndpoint + '/sites').map
      (response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        }
      );
    const unitOfMeasureData = this.http.get(this.baseApiEndpoint + '/uomsystem').map
      (response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        });

    const currencyData = this.http.get(this.baseApiEndpoint + '/currencies').map
      (response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        });

    const countryData = this.http.get(this.baseApiEndpoint + '/countries').map
      (response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        });
    return forkJoin([customerSiteData, unitOfMeasureData, currencyData, countryData]);
  }

  public postCreateCaseData(createCaseData: CreateCaseData) {
    return this.http.post(this.baseApiEndpoint + '/cases', createCaseData).map(response => {
      return response.json();
    }).catch(
      err => {
        return Observable.of(err);
      }
    );
  }

  public getExistingCaseData(caseId: string) {
    return this.http.get(this.baseApiEndpoint + '/cases/' + caseId).map
      (response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        });
  }

  public getPerformanceGaps(caseId: string) {
    return this.http.get(this.baseApiEndpoint + '/case/' + caseId + '/gaps').map
      (response => {
        return response.json();
      });
  }
  public getCustomOpportunities(caseId: string) {
    return this.http.get(this.baseApiEndpoint + '/cases/' + caseId + '/comments').map
      (response => {
        return response.json();
      }).catch(err => {
        return Observable.of(err);
      });
  }

  public postPerformanceGaps(performanceGapsData: PerformanceGapsData) {
    return this.http.post(this.baseApiEndpoint + '/case/gaps', performanceGapsData).map(
      response => {
        return response.json();
      }).catch(err => {
        return Observable.of(err);
      });
  }

  public getGapsSubunit(caseGapId: string) {
    return this.http.get(this.baseApiEndpoint + '/gaps/' + caseGapId + '/subunits').map(
      response => {
        return response.json();
      });
  }

  public getCustomerSiteName(caseId: string) {
    return this.http.get(this.baseApiEndpoint + '/customers/' + caseId + '/customersitename').map(
      response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        });
  }

  public postGapsSubunit(subUnitData: SubUnitData) {
    return this.http.post(this.baseApiEndpoint + '/subunits', subUnitData).map(
      response => {
        return response.json();
      }).catch(err => {
        return Observable.of(err);
      });
  }

  public deleteGaps(caseId: string) {
    return this.http.delete(this.baseApiEndpoint + '/case/gaps/' + caseId).map(response => {
      return response.json()['result'];
    }).catch(err => {
      return Observable.of(err);
    });
  }

  public deleteOpportunity(customOpportunityId: number) {
    return this.http.delete(this.baseApiEndpoint + '/customopportunities/' + customOpportunityId).map(
      response => {
        return response.json()['result'];
      }).catch(err => {
        return Observable.of(err);
      });
  }

  public getEquipmentsData(caseGapId: string) {
    return this.http.get(this.baseApiEndpoint + '/gaps/' + caseGapId + '/equipments').map(response => {
      return response.json();
    }).catch(err => {
      return Observable.of(err);
    });
  }

  public postEquipmentsData(equipmentsData: Equipment) {
    return this.http.post(this.baseApiEndpoint + '/equipments', equipmentsData).map(response => {
      return response.json()['result'];
    }).catch(err => {
      return Observable.of(err);
    });
  }

  public deleteSubunit(gapSubunitId: string) {
    return this.http.delete(this.baseApiEndpoint + '/gaps/' + gapSubunitId + '/subunits').map(response => {
      return response.json()['result'];
    }).catch(err => {
      return Observable.of(err);
    });
  }

  public getDetailsAndInputs(casegapid: string) {
    return this.http.get(this.baseApiEndpoint + '/gaps/' + casegapid + '/detailsandinputs').map(
      response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        }
      );
  }

  public postDetailsData(detailsData) {
    return this.http.post(this.baseApiEndpoint + '/detailsandinputs', detailsData).map(response => {
      return response.json()['result'];
    }).catch(err => {
      return Observable.of(err);
    });
  }
  public postCustomOpportunityData(customOpportunity: CustomOpportunities) {
    return this.http.post(this.baseApiEndpoint + '/customopportunities', customOpportunity).map(
      response => {
        return response.json()['result'];
      }).catch(err => {
        return Observable.of(err);
      });
  }

  public getGapStatus(caseGapId: string) {
    return this.http.get(this.baseApiEndpoint + '/case/gapstatus/' + caseGapId).map(
      response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        });
  }

  public getCalculatedOpportunity(caseId: string) {
    return this.http.get(this.baseApiEndpoint + '/cases/getcalculatedopportunity/' + caseId).map(
      response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        });
  }

  public submitCase(caseId: string) {
    return this.http.post(this.baseApiEndpoint + '/cases/' + caseId + '/submitcase', caseId).map(
      response => {
        return response.json();
      }).catch(err => {
        return Observable.of(err);
      });
  }

  public getCaseDocuments(caseId: string) {
    return this.http.get(this.baseApiEndpoint + '/casedocuments/' + caseId).map(
      response => {
        return response.json();
      }).catch(
        err => {
          return Observable.of(err);
        });
  }

  public deleteCaseDocuments(documentId: number) {
    return this.http.delete(this.baseApiEndpoint + '/casedocuments/' + documentId).map(response => {
      return response.json()['result'];
    }).catch(err => {
      return Observable.of(err);
    });
  }

  public downloadDocument(documentId: number) {
    const url = this.baseApiEndpoint + '/casedocuments/' + documentId + '/download';
    return this.http.get(url, { responseType: ResponseContentType.Blob }).map(
      response => {
        return response;
      }).catch(
        err => {
          return Observable.of(err);
        });
  }

  public downloadAgreement(caseId) {
    const url = this.baseApiEndpoint + '/cases/agreement/' + caseId + '/download';
    return this.http.get(url).map
    (response => {
      return response;
    }).catch(
      err => {
        return Observable.of(err);
      });
  }

  downloadedUploadedAgreement(caseAgreementId) {
    const url = this.baseApiEndpoint + '/cases/uploadedagreement/' + caseAgreementId + '/download';
    return this.http.get(url, { responseType: ResponseContentType.Blob }).map(
      response => {
        return response;
      }).catch(
        err => {
          return Observable.of(err);
        });
  }

  public deleteAgreement(caseagreementid: number) {
    return this.http.delete(this.baseApiEndpoint + '/cases/uploadedagreement/' + caseagreementid).map(
      response => {
        return response.json()['result'];
      }).catch(err => {
        return Observable.of(err);
      });
  }

  public postApproveRejectCase(caseId, isApproved) {
    return this.http.post(this.baseApiEndpoint + '/cases/' + caseId + '/' + isApproved + '/approvrejectcase', '').map(
      response => {
        return response.json();
      }).catch(err => {
        return Observable.of(err);
      });
  }

  public postTerminateCase(caseId) {
    return this.http.post(this.baseApiEndpoint + '/cases/' + caseId + '/' + '/terminatecase', '').map(
      response => {
        return response.json();
      }).catch(err => {
        return Observable.of(err);
      });
  }
}





// WEBPACK FOOTER //
// D:/a/1/s/Fed/src/app/value-tracker/_services/manage-case.service.ts